import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useFeature } from '../hooks/useFeature';
import { getSummaryUpdatePreferences, saveSummaryUpdatePreferences, SUMMARY_UPDATE_MODELS, SummaryUpdatePreferences, SummaryUpdateVersion } from '../utils/summaryUpdatePreferences';

const SummaryUpdateSettings: React.FC = () => {
    const { t } = useTranslation();
    const { user, getDomain } = useAuth();
    const domain = getDomain();
    const enabled = useFeature('summary_update_comparison');
    const [preferences, setPreferences] = useState(() => getSummaryUpdatePreferences(user, domain));
    const [error, setError] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState(false);
    useEffect(() => { setPreferences(getSummaryUpdatePreferences(user, domain)); }, [user, domain]);
    if (!enabled || !user) return null;

    const update = (patch: Partial<SummaryUpdatePreferences>) => {
        try {
            const next = { ...preferences, ...patch };
            saveSummaryUpdatePreferences(user, domain, next);
            setPreferences(next);
            setError(false);
        } catch { setError(true); }
    };

    return (
        <section className="mb-6 space-y-3">
            <h2 className="text-lg font-semibold">{t('userSettings.tabs.developer')}</h2>
            {!unlocked ? <form onSubmit={event => { event.preventDefault(); setUnlocked(code === '200705'); setCodeError(code !== '200705'); }}>
                <label htmlFor="developer-code" className="block mb-2 text-sm">{t('userSettings.developer.code')}</label>
                <input id="developer-code" type="password" inputMode="numeric" maxLength={6} value={code}
                    autoComplete="off" onChange={event => { setCode(event.target.value); setCodeError(false); }} />
                <button className="ml-2 rounded border px-3 py-2" type="submit">{t('userSettings.developer.unlock')}</button>
                {codeError && <p role="alert">{t('userSettings.developer.invalidCode')}</p>}
            </form> : <>
            <button type="button" className="text-sm underline" onClick={() => { setUnlocked(false); setCode(''); }}>{t('userSettings.developer.lock')}</button>
            <label htmlFor="summary-update-version" className="block text-sm font-medium text-gray-700">
                {t('userSettings.summaryUpdates.label')}
            </label>
            <select id="summary-update-version" className="w-full rounded-md border-gray-300" value={preferences.summaryUpdateVersion}
                onChange={event => update({ summaryUpdateVersion: event.target.value as SummaryUpdateVersion })}>
                <option value="v2">{t('userSettings.summaryUpdates.v2')}</option>
                <option value="v3">{t('userSettings.summaryUpdates.v3')}</option>
            </select>
            <label htmlFor="summary-update-model" className="block text-sm font-medium text-gray-700">
                {t('userSettings.summaryUpdates.model')}
            </label>
            <select id="summary-update-model" className="w-full rounded-md border-gray-300" value={preferences.llmModel}
                onChange={event => update({ llmModel: event.target.value })}>
                {SUMMARY_UPDATE_MODELS.map(model => <option key={model} value={model}>{model === 'claude-sonnet-4-6' ? 'Sonnet 4.6' : 'Sonnet 5'}</option>)}
            </select>
            <p className="text-xs text-gray-500">{t('userSettings.summaryUpdates.hint')}</p>
            {error && <p role="alert" className="text-sm text-red-600">{t('userSettings.summaryUpdates.saveError')}</p>}
            </>}
        </section>
    );
};

export default SummaryUpdateSettings;
